import { ProductImageComponent } from '../../shared/product-image/product-image.component'
import { ProductCustomFieldsComponent } from '../../product-custom-fields/product-custom-fields.component'
import {
  Component,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ProductHighlights, ProductOverview } from '../../../types/products'
import { MatDivider } from '@angular/material/divider'
import { RouteService } from '../../../services/route.service'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { ROUTES } from '../../../constants/routes'
import { ProductService } from '../../../services/product.service'
import { SpinnerService } from '../../../services/spinner.service'
import { ErrorService } from '../../../services/error.service'
import { Dialog } from '@angular/cdk/dialog'
import { DialogService } from '../../../services/dialog.service'
import { ApiError } from '../../../types/apiError'
import { catchError, map, of, switchMap } from 'rxjs'
import { WindowService } from '../../../services/window.service'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslationService } from '../../../services/translation.service'
import { MatButtonModule } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { QRCodeComponent } from 'angularx-qrcode'
import { SustainabilityHighlightsComponent } from '../sustainability-highlights/sustainability-highlights.component'
import {
  MatBottomSheet,
  MatBottomSheetModule,
} from '@angular/material/bottom-sheet'
import { RecycledContentSheetComponent } from './recycled-content-sheet/recycled-content-sheet.component'
import { LocationService } from '../../../services/location.service'
import { CANADIAN_PROVINCES } from '../../../constants/provinces'
import { RecyclabilityLocation } from '../../../types/recyclability'
import { SubstancesOfConcernComponent } from '../substances-of-concern/substances-of-concern.component'
@Component({
  selector: 'app-product-overview',
  imports: [
    ProductImageComponent,
    ProductCustomFieldsComponent,
    MatDivider,
    MatTooltipModule,
    MatButtonModule,
    MatIcon,
    QRCodeComponent,
    SustainabilityHighlightsComponent,
    MatBottomSheetModule,
  ],
  templateUrl: './product-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-overview.component.scss',
})
export class ProductOverviewComponent implements OnInit {
  @ViewChild('qrCodeElement') qrCode!: QRCodeComponent

  route = inject(ActivatedRoute)
  router = inject(Router)
  dialog = inject(Dialog)
  dialogService = inject(DialogService)
  routeService = inject(RouteService)
  productService = inject(ProductService)
  spinnerService = inject(SpinnerService)
  errorService = inject(ErrorService)
  windowService = inject(WindowService)
  translationService = inject(TranslationService)
  private _bottomSheet = inject(MatBottomSheet)
  locationService = inject(LocationService)

  productOverview: ProductOverview | undefined

  chemistryUrl: string | undefined
  isMobileView: boolean = false

  qrCodeValue: string = window.location.href

  productHighlights?: ProductHighlights
  currentLocation: RecyclabilityLocation = CANADIAN_PROVINCES[0].name

  async ngOnInit() {
    this.windowService.isMobileView$.subscribe((isMobileView) => {
      this.isMobileView = isMobileView
    })
    this.currentLocation =
      this.locationService.getLocation() ?? CANADIAN_PROVINCES[0].name
    this.spinnerService.startSpinner()
    /*
      Switched from getting the route snapshot to subscribing to params.
      This was due to the fact that the snapshot only retrieved the product id once in ngOnInit.
      So, if I were to view a product, then search in the autocomplete nav for a different product and select it in the dropdown,
      the information of the old product would still show. Subscribing to params resolves that issue.
    */
    this.route
      .parent!.paramMap.pipe(
        switchMap((params: ParamMap) => {
          const id = params.get('id')
          if (id != null) {
            this.chemistryUrl = `/products/${id}/components`
            return this.productService
              .getOverview(id, this.currentLocation)
              .pipe(
                catchError((err) => {
                  this.spinnerService.stopSpinner()
                  const apiErr = err as ApiError
                  this.errorService.handleErr(
                    this.dialogService,
                    apiErr,
                    this.dialog,
                  )
                  return of(null) // Handle the error, return a fallback value
                }),
              )
          } else {
            return of(null) // If no id, return a fallback value
          }
        }),
        map((productOverview) => {
          if (productOverview != null) {
            this.productOverview = productOverview
            const currentRoute = this.routeService.getProductViewRoutes(
              this.productOverview.name,
              ROUTES.PRODUCT_OVERVIEW.path,
              this.productOverview.id,
            )
            this.productService.currentProductData.set({
              id: this.productOverview.id,
              name: this.productOverview.name,
              brand: this.productOverview.brand,
            })

            this.productHighlights = {
              fullIngredientDisclosure:
                this.productOverview.fullIngredientDisclosure,
              recycledContentPercentage:
                this.productOverview.recycledContentPercentage,
              provincialRecyclabilityLabels:
                this.productOverview.provincialRecyclabilityLabels,
              ghsClassifications: this.productOverview.ghsClassifications,
              countryRecyclability: this.productOverview.countryRecyclability,
            }
            this.routeService.setRoute(currentRoute)
          }
        }),
      )
      .subscribe({
        next: () => {
          this.spinnerService.stopSpinner()
        },
        error: (err) => {
          this.spinnerService.stopSpinner()
          const apiErr = err as ApiError
          this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
        },
      })
  }

  /**
   * Downloads the generated QR code for the product
   */
  downloadQRCode() {
    const scaleFactor = 1.5 // this is used to scale the size of the canvas
    const originalCanvas =
      this.qrCode.qrcElement.nativeElement.querySelector('canvas')

    const newCanvas = document.createElement('canvas')
    newCanvas.width = originalCanvas.width * scaleFactor
    newCanvas.height = originalCanvas.height * scaleFactor
    const newCtx = newCanvas.getContext('2d')!

    newCtx.scale(scaleFactor, scaleFactor)
    newCtx.drawImage(originalCanvas, 0, 0)

    const base64Image = newCanvas.toDataURL('image/png')

    const blob = this.convertBase64ToBlob(base64Image)
    const url = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = `${this.productOverview?.name} QR Code.png`
    link.click()

    window.URL.revokeObjectURL(url) // to release memory
  }

  /**
   * Converts a base64 string to a blob
   * @param base64String
   * @returns
   */
  convertBase64ToBlob(base64String: string): Blob {
    const byteString = atob(base64String.split(',')[1])
    const arrayBuffer = new ArrayBuffer(byteString.length)
    const int8Array = new Uint8Array(arrayBuffer)

    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i)
    }

    return new Blob([arrayBuffer], {
      type: 'image/png',
    })
  }

  showRecycledContent() {
    this._bottomSheet.open(RecycledContentSheetComponent, {
      data: {
        recycledContent: this.productOverview?.recycledContent,
      },
    })
  }

  /**
   * Navigates to a product's chemistry screen
   */
  goToProductChemistry(): void {
    this.router.navigate([this.chemistryUrl])
  }

  /**
   * Opens bottom sheet to show substances of concern
   */
  showSubstancesOfConcern() {
    this._bottomSheet.open(SubstancesOfConcernComponent, {
      data: {
        mandatorySubstancesOfConcern:
          this.productOverview?.mandatorySubstancesOfConcern || [],
        voluntarySubstancesOfConcern:
          this.productOverview?.voluntarySubstancesOfConcern || [],
      },
    })
  }
}
